import React, { useState } from "react";
import { Row, Col, Card, Table, Tag, Button, Spin, Tooltip } from "antd";
import {
  TrendingUp,
  ShoppingBag,
  Users,
  Palette,
  MessageSquare,
  RefreshCw,
  ArrowUpRight,
  Eye,
  CheckCircle2,
  Clock,
  Star,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import { useDashboardStats } from "../../../hooks/api/dashboard/hooks";
import OrderDetailModal from "../order/OrderDetailModal";
import { useNavigate } from "react-router-dom";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: statsRes, isLoading, refetch, isRefetching } = useDashboardStats();

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);

  const stats = statsRes?.data || {};
  const summary = stats.summary || {};
  const salesTrend = stats.salesTrend || [];
  const orderStatusData = stats.orderStatus || [];
  const recentOrders = stats.recentOrders || [];
  const recentEnquiries = stats.recentEnquiries || [];

  const renderLocationVal = (val: any): string => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") return val.name || val.title || "";
    return String(val);
  };

  const recentOrderColumns = [
    {
      title: "Order #",
      dataIndex: "orderNumber",
      key: "orderNumber",
      render: (text: string, record: any) => (
        <span
          className="font-semibold text-slate-800 hover:text-primary cursor-pointer"
          onClick={() => {
            setSelectedOrderId(record._id);
            setIsOrderModalOpen(true);
          }}
        >
          {text || record._id}
        </span>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      render: (name: string) => (
        <span className="font-medium text-slate-700">
          {name || "Guest"}
        </span>
      ),
    },
    {
      title: "Total Amount",
      key: "total",
      render: (record: any) => (
        <span className="font-bold text-slate-900">
          £{(record.totalAmount || 0).toLocaleString("en-GB")}
        </span>
      ),
    },
    {
      title: "Payment",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status: string) => {
        let color = "gold";
        if (status === "PAID") color = "green";
        if (status === "FAILED") color = "red";
        return <Tag color={color}>{status || "PENDING"}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (status: string) => {
        let color = "blue";
        if (status === "DELIVERED") color = "green";
        if (status === "CANCELLED") color = "volcano";
        return <Tag color={color}>{status || "CONFIRMED"}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (record: any) => (
        <Button
          type="text"
          size="small"
          icon={<Eye className="w-4 h-4 text-blue-600" />}
          onClick={() => {
            setSelectedOrderId(record._id);
            setIsOrderModalOpen(true);
          }}
        />
      ),
    },
  ];

  const recentEnquiryColumns = [
    {
      title: "Customer",
      dataIndex: "fullName",
      key: "fullName",
      render: (text: string) => <span className="font-medium text-slate-800">{text}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text: string) => (
        <span className="text-slate-600 text-xs truncate max-w-[140px] inline-block">{text || "-"}</span>
      ),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (text: string) => (
        <span className="text-slate-600 truncate max-w-[150px] inline-block">{text || "General Enquiry"}</span>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex h-[75vh] items-center justify-center">
        <Spin size="large" tip="Loading Dashboard Analytics..." />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-12">
      {/* Top Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-6 rounded-2xl shadow-xl">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            Art Parks Executive Dashboard
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Real-time analytics for orders, sculptures, artists, and customer enquiries.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <span className="block text-xs text-slate-400">Today&apos;s Date</span>
            <span className="text-sm font-semibold text-slate-200">
              {dayjs().format("DD MMMM YYYY")}
            </span>
          </div>

          <Button
            type="primary"
            icon={<RefreshCw size={16} className={isRefetching ? "animate-spin" : ""} />}
            onClick={() => refetch()}
            className="!bg-white/10 hover:!bg-white/20 border-white/20 text-white font-medium"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* TOP KEY METRICS CARDS (ROW OF 4 STATS) */}
      <Row gutter={[16, 16]}>
        {/* Total Reviews */}
        <Col xs={24} sm={12} lg={6}>
          <Card className="border border-slate-200/80 hover:shadow-md transition-shadow rounded-xl">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Total Reviews
              </span>
              <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl">
                <TrendingUp size={20} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-extrabold text-slate-900">
                {(summary.totalReviews || 0).toLocaleString()}
              </div>
              <div className="flex items-center gap-1 mt-1 text-xs text-emerald-600 font-medium">
                <ArrowUpRight size={14} /> Customer Reviews
              </div>
            </div>
          </Card>
        </Col>

        {/* Total Orders */}
        <Col xs={24} sm={12} lg={6}>
          <Card className="border border-slate-200/80 hover:shadow-md transition-shadow rounded-xl">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Orders Summary
              </span>
              <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
                <ShoppingBag size={20} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-extrabold text-slate-900">
                {(summary.totalOrders || 0).toLocaleString()}
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 font-medium">
                <span className="text-emerald-600 font-semibold">{summary.completedOrders || 0} Delivered</span>
                <span>•</span>
                <span className="text-amber-600 font-semibold">{summary.pendingOrders || 0} Pending</span>
              </div>
            </div>
          </Card>
        </Col>

        {/* Sculptures & Artists */}
        <Col xs={24} sm={12} lg={6}>
          <Card className="border border-slate-200/80 hover:shadow-md transition-shadow rounded-xl">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Catalog & Artists
              </span>
              <div className="p-2.5 bg-purple-50 text-purple-600 rounded-xl">
                <Palette size={20} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-extrabold text-slate-900">
                {(summary.totalProducts || 0).toLocaleString()}
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 font-medium">
                <span className="font-semibold text-slate-700">Sculptures</span>
                <span>•</span>
                <span className="font-semibold text-purple-600">{summary.totalArtists || 0} Artists</span>
              </div>
            </div>
          </Card>
        </Col>

        {/* Users & Enquiries */}
        <Col xs={24} sm={12} lg={6}>
          <Card className="border border-slate-200/80 hover:shadow-md transition-shadow rounded-xl">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Users & Enquiries
              </span>
              <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
                <MessageSquare size={20} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-extrabold text-slate-900">
                {(summary.totalEnquiries || 0).toLocaleString()}
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 font-medium">
                <span className="text-amber-600 font-semibold">{summary.totalEnquiries || 0} Total Enquiries</span>
                <span>•</span>
                <span className="font-semibold text-blue-600">{summary.totalUsers || 0} Users</span>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* CHARTS SECTION */}
      <Row gutter={[16, 16]}>
        {/* Revenue & Sales Trend Chart (Area Chart) */}
        <Col xs={24} lg={15}>
          <Card
            title={
              <div className="flex items-center justify-between py-1">
                <span className="font-bold text-slate-800 text-base">
                  Orders Overview (Monthly)
                </span>
                <span className="text-xs text-slate-400 font-normal">
                  Past 6 Months Performance
                </span>
              </div>
            }
            className="border border-slate-200/80 shadow-xs rounded-xl"
          >
            {/* Revenue was dropped from this chart - order payment status data isn't
                reliable enough to show as a financial figure (see Oliver's testing
                notes). Order count is a plain record count, not a financial claim. */}
            <div className="h-[320px] w-full pt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={salesTrend.length > 0 ? salesTrend : [{ name: "No Data", orders: 0 }]}>
                  <defs>
                    <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "#0F172A",
                      border: "none",
                      borderRadius: "12px",
                      color: "#FFF",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3)",
                    }}
                    formatter={(val: any) => [val, "Orders Count"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="orders"
                    stroke="#0EA5E9"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorOrders)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>

        {/* Order Status Distribution Chart (Pie Chart) */}
        <Col xs={24} lg={9}>
          <Card
            title={
              <div className="py-1">
                <span className="font-bold text-slate-800 text-base">
                  Order Status Breakdown
                </span>
              </div>
            }
            className="border border-slate-200/80 shadow-xs rounded-xl h-full"
          >
            <div className="h-[320px] w-full flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {orderStatusData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: "#0F172A",
                      border: "none",
                      borderRadius: "8px",
                      color: "#FFF",
                    }}
                  />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      {/* RECENT ORDERS & RECENT ENQUIRIES TABLES SECTION */}
      <Row gutter={[16, 16]}>
        {/* Recent Orders */}
        <Col xs={24} lg={14}>
          <Card
            title={
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-800 text-base flex items-center gap-2">
                  <ShoppingBag size={18} className="text-blue-600" /> Recent Customer Orders
                </span>
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate("/orders")}
                  className="text-xs text-blue-600 font-semibold hover:underline"
                >
                  View All Orders →
                </Button>
              </div>
            }
            className="border border-slate-200/80 shadow-xs rounded-xl"
          >
            <Table
              columns={recentOrderColumns}
              dataSource={recentOrders}
              rowKey={(r: any) => r._id || r.id || String(Math.random())}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* Recent Customer Enquiries */}
        <Col xs={24} lg={10}>
          <Card
            title={
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-800 text-base flex items-center gap-2">
                  <MessageSquare size={18} className="text-amber-600" /> Recent Customer Enquiries
                </span>
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate("/enquiries")}
                  className="text-xs text-amber-600 font-semibold hover:underline"
                >
                  View All →
                </Button>
              </div>
            }
            className="border border-slate-200/80 shadow-xs rounded-xl"
          >
            <Table
              columns={recentEnquiryColumns}
              dataSource={recentEnquiries}
              rowKey={(r: any) => r._id || r.id || String(Math.random())}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* Order Detail Modal for Quick Inspection */}
      <OrderDetailModal
        isOpen={isOrderModalOpen}
        onClose={() => {
          setIsOrderModalOpen(false);
          setSelectedOrderId(null);
        }}
        orderId={selectedOrderId}
      />
    </div>
  );
};

export default Dashboard;
